import React, { useEffect, useState } from "react";
import { Shell } from "../components/Shell";
import { Tabs } from "../components/Tabs";
import { api, ApiError } from "../api";
import { useAuth } from "../auth/AuthContext";

type ActivoFijo = {
  id: number; codigo: string; nombre: string; categoria: string;
  centroCostoId: number | null; centroCostoNombre: string | null;
  proveedorId: number | null; proveedorNombre: string | null;
  fechaAdquisicion: string; valorAdquisicion: number; valorResidual: number; vidaUtilMeses: number;
  estado: "activo" | "dado_de_baja"; fechaBaja: string | null; comprobanteId: number | null;
  depreciacionAcumulada: number; valorLibros: number;
};
type Depreciacion = {
  id: number; activoId: number; activoCodigo: string; activoNombre: string;
  periodo: string; fecha: string; valorDepreciacion: number; comprobanteId: number | null;
};
type MovimientoActivo = {
  id: number; activoId: number; activoCodigo: string; activoNombre: string; tipo: "traslado" | "baja";
  fecha: string; centroCostoOrigenId: number | null; centroCostoDestinoId: number | null;
  motivo: string | null; valorLibrosBaja: number | null; comprobanteId: number | null;
};
type CentroCosto = { id: number; codigo: string; nombre: string };
type CuentaBancaria = { id: number; nombre: string; saldoActual: number };
type Proveedor = { id: number; nombre: string };

const money = (n: number) => `$${Math.round(n).toLocaleString()}`;
const CATEGORIA_LABEL: Record<string, string> = {
  maquinaria: "Maquinaria", vehiculo: "Vehículo", equipo_computo: "Equipo de cómputo",
  muebles_enseres: "Muebles y enseres", edificio: "Edificio", otro: "Otro",
};

export function Activos() {
  const { tienePermiso } = useAuth();
  const puedeCrear = tienePermiso("activos:crear");
  const puedeEditar = tienePermiso("activos:editar");
  const puedeDepreciar = tienePermiso("activos:depreciar");
  const puedeBaja = tienePermiso("activos:baja");

  const [tab, setTab] = useState("activos");
  const [activosList, setActivosList] = useState<ActivoFijo[]>([]);
  const [depreciaciones, setDepreciaciones] = useState<Depreciacion[]>([]);
  const [movimientos, setMovimientos] = useState<MovimientoActivo[]>([]);
  const [centrosCosto, setCentrosCosto] = useState<CentroCosto[]>([]);
  const [cuentas, setCuentas] = useState<CuentaBancaria[]>([]);
  const [proveedores, setProveedores] = useState<Proveedor[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // `esRecarga` evita el parpadeo de "Cargando..." (y el desmontaje de los
  // tabs, que borraría su estado local — p.ej. el mensaje de confirmación de
  // una depreciación recién ejecutada) en recargas posteriores a la primera;
  // solo la carga inicial muestra el estado de carga a pantalla completa.
  const cargar = async (esRecarga = false) => {
    if (!esRecarga) setCargando(true);
    setError(null);
    try {
      const [a, d, m, cc, cb, p] = await Promise.all([
        api.get("/activos"),
        api.get("/activos/depreciaciones"),
        api.get("/activos/movimientos"),
        api.get("/contabilidad/centros-costo").catch(() => []),
        api.get("/tesoreria/cuentas").catch(() => []),
        api.get("/compras/proveedores").catch(() => []),
      ]);
      setActivosList(a); setDepreciaciones(d); setMovimientos(m);
      setCentrosCosto(Array.isArray(cc) ? cc : []); setCuentas(cb); setProveedores(p);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo cargar Activos Fijos.");
    } finally {
      if (!esRecarga) setCargando(false);
    }
  };
  useEffect(() => { cargar(); }, []);
  const recargar = () => cargar(true);

  return (
    <Shell titulo="Activos Fijos" breadcrumb="Activos Fijos">
      <p className="text-sm text-textmuted mb-4">
        Registro de activos fijos (de contado o a crédito con un proveedor), depreciación en línea recta ejecutada
        por período (un comprobante por activo, mismo grano que la liquidación de nómina), traslados entre centros
        de costo y bajas con reconocimiento del valor en libros restante como pérdida. La depreciación acumulada de
        cada activo se calcula en vivo — nunca se guarda como un campo mutable — igual que el resto del sistema.
      </p>
      <Tabs
        tabs={[
          { key: "activos", label: "Activos" },
          { key: "depreciacion", label: "Depreciación" },
          { key: "movimientos", label: "Traslados y bajas" },
        ]}
        activo={tab} onChange={setTab}
      />
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-4">{error}</div>}
      {cargando ? (
        <p className="text-sm text-textmuted">Cargando...</p>
      ) : (
        <>
          {tab === "activos" && (
            <TabActivos
              activos={activosList} centrosCosto={centrosCosto} cuentas={cuentas} proveedores={proveedores}
              puedeCrear={puedeCrear} puedeEditar={puedeEditar} puedeBaja={puedeBaja} recargar={recargar}
            />
          )}
          {tab === "depreciacion" && (
            <TabDepreciacion depreciaciones={depreciaciones} puedeDepreciar={puedeDepreciar} recargar={recargar} />
          )}
          {tab === "movimientos" && <TabMovimientos movimientos={movimientos} />}
        </>
      )}
    </Shell>
  );
}

// ---------------------------------------------------------------- activos
function TabActivos({ activos, centrosCosto, cuentas, proveedores, puedeCrear, puedeEditar, puedeBaja, recargar }: {
  activos: ActivoFijo[]; centrosCosto: CentroCosto[]; cuentas: CuentaBancaria[]; proveedores: Proveedor[];
  puedeCrear: boolean; puedeEditar: boolean; puedeBaja: boolean; recargar: () => void;
}) {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [trasladando, setTrasladando] = useState<ActivoFijo | null>(null);
  const [dandoBaja, setDandoBaja] = useState<ActivoFijo | null>(null);

  const [codigo, setCodigo] = useState("");
  const [nombre, setNombre] = useState("");
  const [categoria, setCategoria] = useState("maquinaria");
  const [centroCostoId, setCentroCostoId] = useState<number | "">("");
  const [fechaAdquisicion, setFechaAdquisicion] = useState("");
  const [valorAdquisicion, setValorAdquisicion] = useState(0);
  const [valorResidual, setValorResidual] = useState(0);
  const [vidaUtilMeses, setVidaUtilMeses] = useState(60);
  const [formaPago, setFormaPago] = useState<"contado" | "credito">("contado");
  const [cuentaBancariaId, setCuentaBancariaId] = useState<number | "">("");
  const [proveedorId, setProveedorId] = useState<number | "">("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!codigo || !nombre || valorAdquisicion <= 0 || vidaUtilMeses <= 0) {
      setError("Código, nombre, valor de adquisición y vida útil son obligatorios.");
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      await api.post("/activos", {
        codigo, nombre, categoria, centroCostoId: centroCostoId || null, fechaAdquisicion,
        valorAdquisicion, valorResidual, vidaUtilMeses, formaPago,
        cuentaBancariaId: formaPago === "contado" ? Number(cuentaBancariaId) || null : null,
        proveedorId: formaPago === "credito" ? Number(proveedorId) || null : null,
      });
      setModalAbierto(false);
      setCodigo(""); setNombre(""); setValorAdquisicion(0); setValorResidual(0);
      setVidaUtilMeses(60); setCuentaBancariaId(""); setProveedorId("");
      recargar();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo registrar el activo.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-3">
        <h3 className="text-sm font-semibold text-slate-800">Activos fijos</h3>
        {puedeCrear && <button className="btn-primary" onClick={() => setModalAbierto(true)}>+ Registrar activo</button>}
      </div>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc">
            <th className="table-th">Código</th><th className="table-th">Nombre</th><th className="table-th">Categoría</th>
            <th className="table-th">Centro de costo</th><th className="table-th">Valor adquisición</th>
            <th className="table-th">Dep. acumulada</th><th className="table-th">Valor en libros</th>
            <th className="table-th">Estado</th><th className="table-th"></th>
          </tr></thead>
          <tbody>
            {activos.map((a) => (
              <tr key={a.id} className="border-b border-borderc last:border-0">
                <td className="table-td font-mono text-xs">{a.codigo}</td>
                <td className="table-td">{a.nombre}</td>
                <td className="table-td text-xs">{CATEGORIA_LABEL[a.categoria] || a.categoria}</td>
                <td className="table-td text-xs">{a.centroCostoNombre || "—"}</td>
                <td className="table-td">{money(a.valorAdquisicion)}</td>
                <td className="table-td text-red-700">{money(a.depreciacionAcumulada)}</td>
                <td className="table-td font-semibold">{money(a.valorLibros)}</td>
                <td className="table-td">
                  <span className={`badge ${a.estado === "activo" ? "bg-accent-light text-accent-dark" : "bg-red-50 text-red-700"}`}>
                    {a.estado === "activo" ? "Activo" : "Dado de baja"}
                  </span>
                </td>
                <td className="table-td text-right whitespace-nowrap">
                  {a.estado === "activo" && puedeEditar && (
                    <button className="text-navy hover:underline text-xs font-semibold mr-3" onClick={() => setTrasladando(a)}>Trasladar</button>
                  )}
                  {a.estado === "activo" && puedeBaja && (
                    <button className="text-red-700 hover:underline text-xs font-semibold" onClick={() => setDandoBaja(a)}>Dar de baja</button>
                  )}
                </td>
              </tr>
            ))}
            {activos.length === 0 && (
              <tr><td colSpan={9} className="table-td text-center text-textmuted py-6">Sin activos fijos registrados.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modalAbierto && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <form onSubmit={crear} className="card w-full max-w-lg p-6 space-y-4 max-h-[85vh] overflow-y-auto">
            <h3 className="text-base font-semibold text-slate-800">Registrar activo fijo</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Código</label>
                <input className="input-field" value={codigo} onChange={(e) => setCodigo(e.target.value)} placeholder="Ej: AF-004" required />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Categoría</label>
                <select className="input-field" value={categoria} onChange={(e) => setCategoria(e.target.value)}>
                  {Object.entries(CATEGORIA_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Nombre</label>
              <input className="input-field" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Centro de costo (opcional)</label>
                <select className="input-field" value={centroCostoId} onChange={(e) => setCentroCostoId(e.target.value ? Number(e.target.value) : "")}>
                  <option value="">Sin asignar</option>
                  {centrosCosto.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Fecha de adquisición</label>
                <input className="input-field" type="date" value={fechaAdquisicion} onChange={(e) => setFechaAdquisicion(e.target.value)} required />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Valor de adquisición</label>
                <input className="input-field" type="number" min={0} step="1000" value={valorAdquisicion} onChange={(e) => setValorAdquisicion(Number(e.target.value))} required />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Valor residual</label>
                <input className="input-field" type="number" min={0} step="1000" value={valorResidual} onChange={(e) => setValorResidual(Number(e.target.value))} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Vida útil (meses)</label>
                <input className="input-field" type="number" min={1} value={vidaUtilMeses} onChange={(e) => setVidaUtilMeses(Number(e.target.value))} required />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Forma de pago</label>
              <select className="input-field" value={formaPago} onChange={(e) => setFormaPago(e.target.value as any)}>
                <option value="contado">De contado (cuenta bancaria)</option>
                <option value="credito">A crédito (proveedor)</option>
              </select>
            </div>
            {formaPago === "contado" ? (
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Cuenta bancaria de origen</label>
                <select className="input-field" value={cuentaBancariaId} onChange={(e) => setCuentaBancariaId(e.target.value ? Number(e.target.value) : "")} required>
                  <option value="">Seleccionar...</option>
                  {cuentas.map((c) => <option key={c.id} value={c.id}>{c.nombre} ({money(c.saldoActual)})</option>)}
                </select>
              </div>
            ) : (
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Proveedor</label>
                <select className="input-field" value={proveedorId} onChange={(e) => setProveedorId(e.target.value ? Number(e.target.value) : "")} required>
                  <option value="">Seleccionar...</option>
                  {proveedores.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                </select>
                <p className="text-[11px] text-textmuted mt-1">
                  Queda contabilizado en Proveedores nacionales, pero no genera una fila individual en el tablero de
                  Cuentas por pagar de Tesorería (limitación conocida — ver README, Fase 13).
                </p>
              </div>
            )}
            {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-ghost" onClick={() => setModalAbierto(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Registrando..." : "Registrar activo"}</button>
            </div>
          </form>
        </div>
      )}

      {trasladando && (
        <ModalTraslado activo={trasladando} centrosCosto={centrosCosto} onClose={() => setTrasladando(null)} onOk={() => { setTrasladando(null); recargar(); }} />
      )}
      {dandoBaja && (
        <ModalBaja activo={dandoBaja} onClose={() => setDandoBaja(null)} onOk={() => { setDandoBaja(null); recargar(); }} />
      )}
    </div>
  );
}

function ModalTraslado({ activo, centrosCosto, onClose, onOk }: {
  activo: ActivoFijo; centrosCosto: CentroCosto[]; onClose: () => void; onOk: () => void;
}) {
  const [centroCostoDestinoId, setCentroCostoDestinoId] = useState<number | "">("");
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const trasladar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!centroCostoDestinoId) return;
    setEnviando(true);
    setError(null);
    try {
      await api.put(`/activos/${activo.id}/trasladar`, { centroCostoDestinoId: Number(centroCostoDestinoId), motivo: motivo || null });
      onOk();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo trasladar el activo.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={trasladar} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Trasladar {activo.codigo} — {activo.nombre}</h3>
        <p className="text-sm text-textmuted">Centro de costo actual: <span className="font-semibold text-slate-800">{activo.centroCostoNombre || "Sin asignar"}</span></p>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Centro de costo destino</label>
          <select className="input-field" value={centroCostoDestinoId} onChange={(e) => setCentroCostoDestinoId(e.target.value ? Number(e.target.value) : "")} required>
            <option value="">Seleccionar...</option>
            {centrosCosto.filter((c) => c.id !== activo.centroCostoId).map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Motivo (opcional)</label>
          <input className="input-field" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Trasladando..." : "Confirmar traslado"}</button>
        </div>
      </form>
    </div>
  );
}

function ModalBaja({ activo, onClose, onOk }: { activo: ActivoFijo; onClose: () => void; onOk: () => void }) {
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const baja = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.put(`/activos/${activo.id}/dar-de-baja`, { motivo: motivo || null });
      onOk();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo dar de baja el activo.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={baja} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Dar de baja {activo.codigo} — {activo.nombre}</h3>
        <p className="text-sm text-textmuted">
          Valor en libros a la fecha: <span className="font-semibold text-slate-800">{money(activo.valorLibros)}</span>.
          {activo.valorLibros > 0 ? " Se reconocerá como pérdida en baja de activos." : " El activo ya está totalmente depreciado — no se reconocerá pérdida."}
        </p>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Motivo (opcional)</label>
          <input className="input-field" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej: dañado, obsoleto, vendido" />
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary bg-red-600 hover:bg-red-700" disabled={enviando}>{enviando ? "Procesando..." : "Confirmar baja"}</button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------- depreciación
function TabDepreciacion({ depreciaciones, puedeDepreciar, recargar }: {
  depreciaciones: Depreciacion[]; puedeDepreciar: boolean; recargar: () => void;
}) {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [periodo, setPeriodo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const ejecutar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!periodo) return;
    setEnviando(true);
    setError(null);
    setMensaje(null);
    try {
      const r = await api.post("/activos/depreciar", { periodo });
      setMensaje(`Período ${r.periodo}: ${r.activosDepreciados} activo(s) depreciado(s) por un total de ${money(r.valorTotal)}.`);
      setModalAbierto(false);
      recargar();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo ejecutar la depreciación.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-3">
        <h3 className="text-sm font-semibold text-slate-800">Depreciaciones registradas</h3>
        {puedeDepreciar && <button className="btn-primary" onClick={() => setModalAbierto(true)}>Depreciar período</button>}
      </div>
      {mensaje && <div className="rounded-md bg-slate-50 border border-borderc px-3 py-2 text-sm text-slate-700 mb-3">{mensaje}</div>}
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc">
            <th className="table-th">Período</th><th className="table-th">Activo</th><th className="table-th">Fecha</th>
            <th className="table-th">Valor depreciado</th>
          </tr></thead>
          <tbody>
            {depreciaciones.map((d) => (
              <tr key={d.id} className="border-b border-borderc last:border-0">
                <td className="table-td font-semibold">{d.periodo}</td>
                <td className="table-td">{d.activoCodigo} — {d.activoNombre}</td>
                <td className="table-td text-xs">{d.fecha}</td>
                <td className="table-td">{money(d.valorDepreciacion)}</td>
              </tr>
            ))}
            {depreciaciones.length === 0 && (
              <tr><td colSpan={4} className="table-td text-center text-textmuted py-6">Sin depreciaciones registradas todavía.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modalAbierto && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <form onSubmit={ejecutar} className="card w-full max-w-sm p-6 space-y-4">
            <h3 className="text-base font-semibold text-slate-800">Depreciar período</h3>
            <p className="text-sm text-textmuted">
              Deprecia en línea recta todos los activos vigentes que aún no se hayan depreciado en este período. Es
              seguro reintentar: los activos ya depreciados en el período se omiten automáticamente.
            </p>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Período (AAAA-MM)</label>
              <input className="input-field" type="month" value={periodo} onChange={(e) => setPeriodo(e.target.value)} required />
            </div>
            {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-ghost" onClick={() => setModalAbierto(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Procesando..." : "Ejecutar depreciación"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- traslados y bajas
function TabMovimientos({ movimientos }: { movimientos: MovimientoActivo[] }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-800 mb-3">Traslados y bajas</h3>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc">
            <th className="table-th">Tipo</th><th className="table-th">Activo</th><th className="table-th">Fecha</th>
            <th className="table-th">Detalle</th><th className="table-th">Pérdida en baja</th>
          </tr></thead>
          <tbody>
            {movimientos.map((m) => (
              <tr key={m.id} className="border-b border-borderc last:border-0">
                <td className="table-td">
                  <span className={`badge ${m.tipo === "baja" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>{m.tipo}</span>
                </td>
                <td className="table-td">{m.activoCodigo} — {m.activoNombre}</td>
                <td className="table-td text-xs">{m.fecha}</td>
                <td className="table-td text-xs">{m.motivo || "—"}</td>
                <td className="table-td">{m.valorLibrosBaja != null ? money(m.valorLibrosBaja) : "—"}</td>
              </tr>
            ))}
            {movimientos.length === 0 && (
              <tr><td colSpan={5} className="table-td text-center text-textmuted py-6">Sin traslados ni bajas registrados todavía.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
